import React from "react";
import { useSelector } from "react-redux";
import { getIn } from "formik";

import { getBusinessById, getBusinessRelationsByBusinessId } from "selectors";
import { getBusinessLabels } from "selectors/root";
import { selectVendorCommissionBandIdsByBusinessIdLookup } from "selectors/indexes";
import { pluralize } from "lib/pluralize";

import {
  fieldConfigurations as baseFieldConfigurations,
  FieldConfiguration as BaseFieldConfiguration,
  FieldGroup,
  DEFAULT_SIDE_LOADED_USER_ROLES,
  NOTES_FIELD_INDICES,
  PIC_FIELD_INDICES,
  groupConfigurationsByGroup,
  FieldConfigurationGroup,
} from "lib/sideLoadedBusinesses/fieldConfigurations";

import { BusinessLabelChips } from "./BusinessLabelChips";

export {
  FieldGroup,
  DEFAULT_SIDE_LOADED_USER_ROLES,
  NOTES_FIELD_INDICES,
  PIC_FIELD_INDICES,
  groupConfigurationsByGroup,
};
export type { FieldConfigurationGroup };

export interface FieldConfiguration extends BaseFieldConfiguration {
  businessValueRenderer?: React.ComponentType<{
    businessId?: string | null;
  }> | null;
}

function SimpleBusinessValueRendererFactory(fieldName: string) {
  const SimpleBusinessValueRenderer = ({ businessId }) => {
    const business = useSelector(getBusinessById(businessId)) || null;
    const value = getIn(business, fieldName) || "-";
    return <span>{value}</span>;
  };
  return SimpleBusinessValueRenderer;
}

const EmailRecipientsValueRenderer = ({ businessId }) => {
  const business = useSelector(getBusinessById(businessId)) || null;
  if (!business) {
    return null;
  }
  return (
    <span>
      {business.emailRecipients.length} existing{" "}
      {pluralize("contact", business.emailRecipients.length)}.
    </span>
  );
};

const LabelsValueRenderer = ({ businessId }) => {
  const business = useSelector(getBusinessById(businessId)) || null;
  const businessLabelsById = useSelector(getBusinessLabels);
  const names = (business?.labelIds || [])
    .map(labelId => businessLabelsById[labelId]?.name)
    .filter(Boolean);
  return <BusinessLabelChips names={names} />;
};

const PropertyValueRenderer = ({ businessId }) => {
  const business = useSelector(getBusinessById(businessId)) || null;
  const shownProperties = (business?.properties || []).filter(
    property => property.isShown,
  );
  if (!shownProperties.length) {
    return null;
  }
  return (
    <span>
      {shownProperties.length} existing{" "}
      {pluralize("property", shownProperties.length)}.
    </span>
  );
};

const VendorPercentageCommissionValueRenderer = ({ businessId }) => {
  const vendorCommissionBandIdsByBusinessIdLookup = useSelector(
    selectVendorCommissionBandIdsByBusinessIdLookup,
  );
  const vendorCommissionBandIds =
    vendorCommissionBandIdsByBusinessIdLookup[businessId] || [];
  return (
    <span>
      {vendorCommissionBandIds.length} existing{" "}
      {pluralize("vendor commission band", vendorCommissionBandIds.length)}.
    </span>
  );
};

const RelationshipValueRenderer = ({ businessId }) => {
  const businessRelationsByBusinessId = useSelector(
    getBusinessRelationsByBusinessId,
  );
  const businessRelations = businessRelationsByBusinessId[businessId] || [];
  return (
    <span>
      {businessRelations.length} existing{" "}
      {pluralize("business relation", businessRelations.length)}.
    </span>
  );
};

const CUSTOM_RENDERERS: Record<
  string,
  React.ComponentType<{ businessId?: string | null }> | null
> = {
  contact_email_1: EmailRecipientsValueRenderer,
  labels: LabelsValueRenderer,
  pics: PropertyValueRenderer,
  vendorPercentageCommission: VendorPercentageCommissionValueRenderer,
  stockAgent: RelationshipValueRenderer,
  rebateAgent: null,
  stockAgentPercentageCommission: null,
  rebateAgentPercentageCommission: null,
  transitInsurance: null,
};

export const fieldConfigurations: Record<string, FieldConfiguration> =
  Object.entries(baseFieldConfigurations).reduce(
    (acc, [key, configuration]) => {
      let businessValueRenderer:
        | React.ComponentType<{ businessId?: string | null }>
        | null
        | undefined = CUSTOM_RENDERERS[configuration.value];

      if (businessValueRenderer === undefined) {
        if (configuration.handleAsContact) {
          businessValueRenderer = null;
        } else if (configuration.targetFieldName) {
          businessValueRenderer = SimpleBusinessValueRendererFactory(
            configuration.targetFieldName,
          );
        } else {
          businessValueRenderer = null;
        }
      }

      acc[key] = {
        ...configuration,
        businessValueRenderer,
      };
      return acc;
    },
    {} as Record<string, FieldConfiguration>,
  );

export function getSideLoadedFieldConfigurationsForUser(
  userRoleContentTypeName: string | null,
): FieldConfiguration[] {
  if (userRoleContentTypeName === null) {
    return [];
  }
  return Object.values(fieldConfigurations).filter(configuration =>
    configuration.forUserRoles.includes(userRoleContentTypeName),
  );
}
